import * as bootstrap from 'bootstrap';
import {cookiesSettings} from "./cookiesSettings";

const jsKit = (() => {
  const defaults = {
    modalID: '#mAll',
    debugMode: false,
  };

  const currentAbortController = null;

  const rwdMedia = {};
  const delegatedEvents = {};
  let delegatedEventOrder = 0;

  ['xs', 'sm', 'md', 'lg', 'xl', 'xxl'].forEach((bp) => rwdMedia[bp] = () => document.querySelector(`.media-${bp}`).offsetParent !== null);

  return {
    currentAbortController,

    defaults,

    rwdMedia,

    isIOS: () => /iPhone|iPad|iPod/i.test(navigator.userAgent),

    isAndroid: () => /Android/i.test(navigator.userAgent),

    isMobile: () => jsKit.isIOS() || jsKit.isAndroid(),

    getIndex: (nodeList, element) => Array.from(nodeList).indexOf(element),

    fetchUrl: async (url, cb, responseType = 'text', method = 'GET', data = null, requestType = 'urlencoded', options = {}) => {
      const headers = {};
      const { abortPrevious = false } = options;
      const abortController = new AbortController();

      if (abortPrevious && jsKit.currentAbortController) {
        jsKit.currentAbortController.abort();
      }

      jsKit.currentAbortController = abortController;

      let body;

      if (method === 'POST' && data != null) {
        if (data instanceof FormData) {
          body = data;
        } else if (requestType === 'json') {
          headers['Content-Type'] = 'application/json';
          body = JSON.stringify(data);
        } else {
          headers['Content-Type'] = 'application/x-www-form-urlencoded; charset=UTF-8';
          body = new URLSearchParams(data).toString();
        }
      }

      headers['X-Requested-With'] = 'XMLHttpRequest';

      try {
        const response = await fetch(url, {
          method,
          headers,
          body,
          signal: abortController.signal,
          credentials: 'same-origin',
        });

        let result;

        if (responseType === 'json') {
          result = await response.json();
        } else if (responseType === 'xml') {
          const text = await response.text();
          const trimmed = (text || '').trim();
          const xmlRootPattern = /^\s*<\?xml[\s\S]*?\?>\s*<\s*z[\s>]|^\s*<\s*z[\s>]/i;

          if (!trimmed || !xmlRootPattern.test(trimmed)) {
            if (defaults.debugMode && trimmed) {
              console.warn('fetchUrl() expected XML but got non-XML response', url, trimmed.slice(0, 200));
            }

            result = document.implementation.createDocument('', 'z', null);
          } else {
            result = new DOMParser().parseFromString(trimmed, 'text/xml');
          }
        } else {
          result = await response.text();
        }

        cb(result);
        return result;
      } catch (error) {
        if (error?.name === 'AbortError') {
          if (defaults.debugMode) {
            console.debug('fetchUrl() aborted', url);
          }
          return null;
        }

        throw error;
      } finally {
        if (jsKit.currentAbortController === abortController) {
          jsKit.currentAbortController = null;
        }
      }
    },
    initDynamicContent: (scope = document) => {
      if (typeof window.initDynamicContent === 'function') {
        window.initDynamicContent(scope);
      }
    },

    historyBack: (e) => {
      e.preventDefault();
      window.history.back()
    },

    closeModal: () => {
      const domLargeModal = document.querySelector(defaults.modalID);
      const isOpen = domLargeModal.classList.contains('show');
      const bsModal = bootstrap.Modal.getOrCreateInstance(domLargeModal);
      if (isOpen) {
        bsModal.hide();
      }
    },

    emptyModal: () => {
      const domLargeModal = document.querySelector(defaults.modalID);
      domLargeModal.innerHTML = '';
    },

    showModal: (modalID, options = {}) => {
      if (!document.querySelector(modalID)) {
        return;
      }

      const myModal = document.querySelector(modalID);
      const isOpen = myModal.classList.contains('show');
      const bsModal = bootstrap.Modal.getOrCreateInstance(myModal);
      if (isOpen) {
        bsModal.hide();
      }

      myModal.addEventListener('shown.bs.modal', event => {
        if (typeof options.shownCB === 'function') {
          options.shownCB();
        }
      });

      myModal.addEventListener('hidden.bs.modal', event => {
        if (typeof options.hiddenCB === 'function') {
          options.hiddenCB();
        }
      });

      bsModal.show();
    },

    openModal: (html, customs = {}) => {
      if (defaults.debugMode) {
        console.log('openModal()', customs);
      }

      const defaultOptions = {
        size: 'modal-lg',
        config: { backdrop: 'true' },
        shownCB: false,
        hiddenCB: false,
      };
      const options = { ...defaultOptions, ...customs};
      const domLargeModal = document.querySelector(defaults.modalID);
      const isOpen = domLargeModal.classList.contains('show');
      const bsModal = bootstrap.Modal.getOrCreateInstance(domLargeModal);

      if (isOpen) {
        bsModal.hide();
      }

      domLargeModal.addEventListener('shown.bs.modal', event => {
        jsKit.autofocus(domLargeModal);
        if (typeof options.shownCB === 'function') {
          options.shownCB();
        }
      });

      domLargeModal.addEventListener('hidden.bs.modal', event => {
        jsKit.emptyModal();
        if (typeof options.hiddenCB === 'function') {
          options.hiddenCB();
        }
      });

      //domLargeModal.querySelector('.modal-dialog').classList.add(options.size);
      jsKit.emptyModal();
      domLargeModal.innerHTML = html;
      jsKit.initDynamicContent(domLargeModal);
      bsModal.show();
    },

    modalData: async (e) => {
      if (defaults.debugMode) {
        console.log('modalData()');
      }

      const { target } = e;
      jsKit.showLoader(e);

      const url = target.dataset.jsModalData;
      if (!url) return;

      const getCB = (html) => {
        jsKit.openModal(html);
        jsKit.hideLoader(e);
      };
      await jsKit.fetchUrl(url, (html) => getCB(html));
    },

    modalCookies: async (e) => {
      if (defaults.debugMode) {
        console.log('modalCookies()');
      }

      const { target } = e;

      const url = target.dataset.jsModalCookies;
      if (!url) return;

      const getCB = (html) => {
        jsKit.openModal(html, {shownCB: () => cookiesSettings()});
      };
      await jsKit.fetchUrl(url, (html) => getCB(html));
    },

    containerData: async (e) => {
      if (defaults.debugMode) {
        console.log('containerData()');
      }

      const { target } = e;
      jsKit.showLoader(e);

      const [url, containerId] = target.dataset.jsContainerData.split(', ');

      const container = document.getElementById(containerId);
      if (!container) return;

      const getCB = (html) => {
        container.innerHTML = html;
        jsKit.initDynamicContent(container);
        jsKit.hideLoader(e);
      };
      await jsKit.fetchUrl(url, html => getCB(html));
    },

    getDataForm: async (e) => { // getDataForm
      if (defaults.debugMode) {
        console.log('getDataForm()');
      }

      const { target } = e;
      jsKit.showLoader(e);

      const [url, containerId, fieldId ] = target.dataset.jsGetDataForm.split(', ');

      const container = document.getElementById(containerId);
      const field = document.getElementById(fieldId);
      if (!container || !field) return;

      const getCB = (html) => {
        container.innerHTML = html;
        jsKit.initDynamicContent(container);
        jsKit.hideLoader(e);
      };
      await jsKit.fetchUrl(`${url}${field.value}`, html => getCB(html));
    },

    getTarget: (eOrEl) => {
      const target =
          eOrEl instanceof Event
              ? eOrEl.target
              : eOrEl instanceof HTMLElement
                  ? eOrEl
                  : null;
      if (!target) {
        console.warn('loadCollapse(): brak targetu');
        return;
      }

      return target;
    },

    showLoader:(eOrEl) => {
      const target = jsKit.getTarget(eOrEl);
      const isBtn = target.classList.contains('btn');
      if (isBtn) {
        target.classList.add('loading');
        return;
      }

      const parentLoader = target.closest('[data-parent-loader]');

      if (parentLoader) {
        const elemId = parentLoader.dataset.parentLoader;
        if (elemId) {
          document.querySelector(`#${elemId}`).classList.add('loading');
          return;
        }
        parentLoader.classList.add('loading');
      }

    },

    hideLoader:(eOrEl) => {
      const target = jsKit.getTarget(eOrEl);
      const isBtn = target.classList.contains('btn');
      if (isBtn) {
        target.classList.remove('loading');
        return;
      }

      const parentLoader = target.closest('[data-parent-loader]');

      if (parentLoader) {
        const elemId = parentLoader.dataset.parentLoader;
        if (elemId) {
          document.querySelector(`#${elemId}`).classList.remove('loading');
          return;
        }
        parentLoader.classList.remove('loading');
      }
    },

    autofocus: (parentElem) => {
      const autofocusInput = parentElem.querySelector('[autofocus]');
      if (autofocusInput && !jsKit.isMobile()) {
        autofocusInput.focus();

        if (autofocusInput.tagName === 'INPUT' && autofocusInput.type === 'text') {
          const length = autofocusInput.value.length;
          autofocusInput.setSelectionRange(length, length);
        }
      }
    },

    initTooltip: (tooltipTriggerList) => {
      [...tooltipTriggerList].map((tooltipTriggerEl) => new bootstrap.Tooltip(tooltipTriggerEl));
    },

    initPopover: (popoverTriggerList) => {
      [...popoverTriggerList].map((popoverTriggerEl) => new bootstrap.Popover(popoverTriggerEl));
    },

    toKebabCase: (str) => str.replace(/([a-z0-9]|(?=[A-Z]))([A-Z])/g, '$1-$2').toLowerCase(),

    runIfDataSetExists: (dataItem, fn) => {
      const selector = `[data-${jsKit.toKebabCase(dataItem)}]`;
      const el = document.querySelector(selector);

      if (el && typeof fn === 'function') {
        fn();
      }
    },

    capitalize: (str) =>  str.charAt(0).toUpperCase() + str.slice(1),

    attachEventByDataSet: (dataItem, fn, eventName = 'click') => {
      if (eventName === 'DOMContentLoaded') {
        jsKit.runIfDataSetExists(dataItem, fn);
        return;
      }

      delegatedEvents[eventName] = delegatedEvents[eventName] || {
        handlers: {},
        isBound: false,
      };

      delegatedEvents[eventName].handlers[dataItem] = delegatedEvents[eventName].handlers[dataItem] || [];
      delegatedEvents[eventName].handlers[dataItem].push({
        dataItem,
        fn,
        order: delegatedEventOrder,
      });
      delegatedEventOrder += 1;

      if (delegatedEvents[eventName].isBound) return;
      delegatedEvents[eventName].isBound = true;

      const useCapture = eventName === 'blur' || eventName === 'focus';

      document.addEventListener(eventName, (e) => {
          const eventConfig = delegatedEvents[eventName];
          const handlers = eventConfig ? eventConfig.handlers : {};
          const matches = [];
          const matchedDataItems = new Set();
          const target = e.target;
          let node = target && target.nodeType === Node.ELEMENT_NODE ? target : target && target.parentElement;

          while (node && node !== document) {
            if (node.dataset) {
              Object.keys(node.dataset).forEach((item) => {
                if (!handlers[item] || matchedDataItems.has(item)) return;

                matchedDataItems.add(item);
                handlers[item].forEach((handlerConfig) => {
                  matches.push({
                    ...handlerConfig,
                    el: node,
                  });
                });
              });
            }

            node = node.parentElement;
          }

          matches
            .sort((a, b) => a.order - b.order)
            .forEach(({ dataItem: item, fn: handler, el }) => {
              if (typeof handler !== 'function') return;

              e.delegateTarget = el;
              e.delegateDataItem = item;

              if (defaults.debugMode) {
                console.log('attachEventByDataSet match', item, eventName, el);
              }

              handler(e);
            });
        }, useCapture);
    },

    validateInput: (input) => {
      const pattern = input.dataset.jsValidate;
      const invalidText = input.dataset.jsInvalidText;
      const invalidFeedbackElem = input.nextElementSibling;
      if (defaults.debugMode) {
        console.log(`inputValid(${input})`);
      }

      const regex = new RegExp(`^${pattern}$`);
      if (regex.test(input.value)) {
        input.classList.remove('is-invalid');
      } else {
        input.classList.add('is-invalid');
        invalidFeedbackElem.innerText = invalidText;
      }

      return (regex.test(input.value));
    },
  };
})();

export default jsKit;
